/* oxlint-disable typescript-eslint/prefer-for-of -- this module ships in the CloudFront Functions edge bundle, and cloudfront-js-2.0 rejects `for-of` (`SyntaxError: Token "of" not supported`). Verified via aws cloudfront test-function probes. */
import { fnv1a32 } from './fnv1a32';

const RATIO_DIVISOR = 0x1_0000_0000;

function ratio(bucketId: string, flagKey: string, salt: string): number {
  return fnv1a32(`${bucketId}:${flagKey}:${salt}`) / RATIO_DIVISOR;
}

/**
 * Is this bucket IN the experiment at all?
 *
 * `enrollment` is the fraction of traffic enrolled (e.g. `0.1` = 10%).
 * Non-enrolled users see the flag's default and DO NOT fire exposure events.
 *
 * Uses a different salt than `pickVariant` so enrollment and variant
 * decisions are independent for the same bucket+flag pair.
 */
export function isEnrolled(
  bucketId: string,
  flagKey: string,
  enrollment: number,
): boolean {
  return ratio(bucketId, flagKey, 'enroll') < enrollment;
}

/**
 * Pick a variant for an enrolled bucket.
 *
 * `split` maps variant name to weight; weights are expected to sum to 1.0
 * (validation is the caller's responsibility). Returns the variant whose
 * cumulative range covers the bucketed value. Falls back to the last entry
 * if rounding leaves us on the upper bound.
 */
export function pickVariant<V extends string>(
  bucketId: string,
  flagKey: string,
  split: Readonly<Record<V, number>>,
): V {
  const r = ratio(bucketId, flagKey, 'variant');
  const entries = Object.entries(split) as [V, number][];
  if (entries.length === 0) {
    // Terse message: this string ships in the byte-budgeted edge bundle.
    throw new Error(`empty split: ${flagKey}`);
  }
  let cumulative = 0;
  // Classic indexed for-loop without an uninitialized `let` binding.
  // The cloudfront-js-2.0 parser rejects `for-of`, destructuring bindings,
  // AND uninitialized `let` declarations (`let x;`). We can't write
  // `let last = entries[0][0]` and update inside the loop because the
  // minifier collapses `let last = undefined` back to `let s;`, so we
  // restructure to a guaranteed-return shape instead.
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (entry === undefined) break;
    cumulative += entry[1];
    if (r < cumulative) return entry[0];
  }
  // Rounding edge: r is past the cumulative sum. Return the last entry.
  // entries.length > 0 was checked above, so this index is in-bounds; the
  // explicit narrow appeases `noUncheckedIndexedAccess`. Index access
  // rather than `.at(-1)` because cloudfront-js-2.0's runtime docs
  // enumerate Array prototype methods through ES7 `includes` and do NOT
  // list ES2022 `Array.prototype.at` — absence on an allowlist means
  // "don't rely on it." Index access is ES5 and universally supported.
  // oxlint-disable-next-line unicorn/prefer-at -- see comment above; `.at(-1)` is unsafe in cloudfront-js-2.0
  const tail = entries[entries.length - 1];
  if (tail === undefined) {
    // Terse message: this string ships in the byte-budgeted edge bundle.
    throw new Error(`empty split: ${flagKey}`);
  }
  return tail[0];
}

/**
 * The variant that absorbed a retired variant's hash range.
 *
 * Retiring a variant means zeroing its weight and giving its share to the
 * variant immediately before it (`docs/guidelines/web/edge-bucketed-experiments.md`,
 * "Retiring an arm"), which is what keeps every surviving cumulative boundary
 * where it was. `pickVariant` assigns by cumulative range, so under that rule
 * the bucket ids that used to land on the retired variant now land on the
 * nearest PRECEDING variant the split still assigns — and that is what this
 * returns. Derived from the split rather than declared next to it, so the fold
 * and its consumers cannot drift apart.
 *
 * It matters because the two surfaces of an `at-signup` experiment read
 * different things: a cached marketing page renders the arm the visitor's
 * cookie hashes to now, while the paywall quotes the account's pin. Serving a
 * retired pin this variant is what makes those two the same answer for a
 * visitor still holding the cookie they were pinned from.
 *
 * `null` when no live variant precedes the retired one: nothing absorbed the
 * range, and the caller falls back to whatever it serves an unassignable
 * viewer. `retired` is a plain string because it arrives from durable data — a
 * pinned row outlives the definition it was written from — so a name this split
 * does not list is an ordinary null, not a type error.
 */
export function absorbingVariant<V extends string>(
  split: Readonly<Record<V, number>>,
  retired: string,
): V | null {
  const entries = Object.entries(split) as [V, number][];
  let absorbed = null as V | null;
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (entry === undefined) break;
    if (entry[0] === retired) return absorbed;
    if (entry[1] > 0) absorbed = entry[0];
  }
  return null;
}
